import { useEffect, useId } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapProps } from '../../services/Models/ICharts';
import londonBoroughs from '../../services/Data/Charts/londonBoroughs.json'
import { FeatureCollection } from 'geojson';
import * as d3 from 'd3';

export const Map = (props: MapProps) => {
    const uniqueId = useId();
    const mapId = `map-${uniqueId}`;

    const MAPBOX_ACCESS_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN
    console.log("londonBoroughs", londonBoroughs)

    useEffect(() => {
        const londonBounds = [
            [51.286760, -0.510375],
            [51.691874, 0.334015]
        ];

        // Create map instance
        const map = L.map(mapId, {
            attributionControl: false,
            scrollWheelZoom: true,
            zoomControl: true,
            dragging: true,
            doubleClickZoom: true,
            maxBounds: londonBounds,
            maxBoundsViscosity: 1.0,
            zoom: 10,
            minZoom: 10,
            maxZoom: 17,
        }).setView([51.505, -0.09], 10);

        // Add OpenStreetMap tiles
        L.tileLayer(`https://api.mapbox.com/styles/v1/mapbox/streets-v11/tiles/{z}/{x}/{y}?access_token=${MAPBOX_ACCESS_TOKEN}`, {
            maxZoom: 17,
            attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        }).addTo(map);

        // TODO: update map colour scheme to be aligned with charts
        const values = props.data.map(d => d.value);
        const colorScale = d3.scaleSequential()
            .domain([Math.min(...values), Math.max(...values)])
            // .interpolator(d3.interpolateTurbo); // Yellow to Red color scheme
            .interpolator(d3.interpolateRgbBasis(["aqua", "green", "blue", "purple"])); // Yellow to Red color scheme

        // Create legend
        const Legend = L.Control.extend({
            options: {
                position: 'bottomright'
            },

            onAdd: function () {
                const div = L.DomUtil.create('div', 'legend');
                div.style.backgroundColor = 'white';
                div.style.padding = '10px';
                div.style.borderRadius = '5px';

                const steps = 5;
                const delta = (Math.max(...values) - Math.min(...values)) / (steps - 1);

                div.innerHTML += '<h4 class="font-bold mb-2">Values</h4>';

                for (let i = 0;i < steps;i++) {
                    const value = Math.min(...values) + (delta * i);
                    const color = colorScale(value);
                    div.innerHTML += `
            <div class="flex items-center gap-2">
              <div style="background: ${color}; width: 20px; height: 20px;"></div>
              <span>${Math.round(value)}</span>
            </div>
          `;
                }

                return div;
            }
        });

        // Add legend to map
        new Legend().addTo(map);

        L.geoJSON(londonBoroughs as FeatureCollection, {
            style: (feature) => {
                if (!feature) return {};
                const boroughData = props.data.find(d => d.borough === feature.properties.name);
                const value = boroughData ? boroughData.value : 0;

                return {
                    color: 'white',
                    weight: 2,
                    // fillColor: colorScale(feature.properties.name),
                    fillColor: colorScale(value),
                    fillOpacity: 0.5
                };
            },
            onEachFeature: (feature, layer) => {
                const boroughData = props.data.find(d => d.borough === feature.properties.name);
                const value = boroughData ? boroughData.value : 0;

                layer.bindPopup(`
            <div class="p-2">
              <strong>${feature.properties.name}</strong>
              <br/>
              Value: ${value.toFixed(2)}
            </div>
          `);
                layer.on('mouseover', (e) => {
                    const l = e.target;
                    // l.bindTooltip(feature.properties.name, {
                    //     permanent: false,
                    //     direction: 'center'
                    // }).openTooltip();
                    l.setStyle({
                        weight: 2,
                        fillOpacity: 0.7
                    });
                    l.openPopup();
                });

                layer.on('mouseout', (e) => {
                    const l = e.target;
                    l.setStyle({
                        weight: 1,
                        fillOpacity: 0.4
                    });
                    l.closePopup();
                });
            }
        }).addTo(map);

        // Cleanup on unmount
        return () => {
            map.remove();
        };
    }, [MAPBOX_ACCESS_TOKEN, mapId, props.data]); // Re-initialize if center or zoom changes

    return (
        <>
            <div className="flex flex-col gap-4 mb-8">
                <h2 className="text-l">{props.title}</h2>
                <div
                    id={mapId}
                    className="rounded-lg shadow-lg"
                    style={{ height: `${props.height}px`, width: `${props.width}px` }}
                />
            </div>
        </>
    );
};